import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { AuthProvider } from './components/AuthProvider';
import App from './App';
import './index.css';
import './i18n';
import { captureUtmsFromUrl } from './lib/utm';
import { installDomGuard } from './lib/domGuard';

installDomGuard();
captureUtmsFromUrl();

// Stale chunks after a deploy (TSF-213, 2026-09-10: "Failed to fetch dynamically imported
// module") are handled by lazyPage() in src/lib/chunkReload.ts, which reloads once. The old
// vite:preloadError handler here always called preventDefault(): Vite then resolved the import
// with `undefined` and React crashed on "reading 'default'" — a blank page inside its 10 s guard.

// Public client-side ingest token for the Sport Fund PostHog project (192640,
// EU). It is safe to commit (public write key). Kept as a hardcoded fallback so
// a clean/CI build — where the gitignored .env.*.local files are absent and the
// tracked .env.production carries no token — never resolves to an empty key and
// silently ships analytics-blind.
const POSTHOG_TOKEN_FALLBACK = 'phc_odWeR8XKSUoVHJgsXmuStvxkttMiV2U76JqQNTGdQ3GW';

const posthogKey =
  (import.meta.env.VITE_POSTHOG_PROJECT_TOKEN as string | undefined) ||
  POSTHOG_TOKEN_FALLBACK;
// CZ-61: hardcoded, NOT read from VITE_POSTHOG_HOST. CI writes the repo secret
// ENV_PRODUCTION_LOCAL into .env.production.local, which Vite ranks ABOVE the
// committed .env.production — and that secret still carried the direct host, so
// the live bundle kept shipping eu.i.posthog.com (ad-blocked) even after
// .env.production moved to the relay. The relay is same-origin
// (firebase.json /ingest/** -> posthogProxy); a capture POSTed through it on
// 2026-09-13 returned 200 {"status":"Ok"}. Dev builds keep the direct host.
const posthogHost = import.meta.env.PROD
  ? 'https://thesportfund.com/ingest'
  : 'https://eu.i.posthog.com';

// ── Initialise PostHog at MODULE LOAD (two-tier, cookieless by default) ──────
// Not inside a component/effect: child effects run before parent effects, so a
// $pageview fired on first render would be dropped if init hadn't run yet.
//
// TTDSG §25 + GDPR, two-tier by design:
//   DEFAULT (no consent decision) — BASIC, COOKIELESS, ANONYMOUS reach:
//     persistence:"memory" + disable_persistence:true write NOTHING to the
//     device, so §25 is not triggered and no consent is required. Page views +
//     unique visitors are lawful under Art.6(1)(f) legitimate interest,
//     minimised (EU-hosted, no persistent identifier, session recording OFF).
//     We therefore capture by DEFAULT — no opt_out_capturing_by_default.
//   ON "Accept" (ConsentBanner) — PERSISTENT upgrade: localStorage+cookie
//     (cross-session identity + funnel tracing) + session recording on.
//   ON "Decline" — opt out of everything (handled just below).
// No respect_dnt: browser Do-Not-Track has no legal force in the EU and would
// silently blind analytics even for visitors who clicked "Accept".
// Skip entirely during the Puppeteer prerender pass (build-time static
// snapshot, see PRERENDER_ROUTES in vite.config.ts): it executes this same
// client module in headless Chrome for every route, so without this guard
// each production build fired one real $pageview/$autocapture pair per
// prerendered route into PostHog — noise indistinguishable from a real
// visitor on a product whose real traffic is ~1-5 visitors/day. Same
// navigator.webdriver detection ConsentBanner.tsx already uses to skip
// rendering the banner during prerender.
const isPrerenderPass =
  typeof navigator !== 'undefined' && navigator.webdriver;

// The Control Center's own ConceptZoneProbe uptime monitor: that headless
// prober runs with navigator.webdriver suppressed specifically so it reads as
// human traffic, so the webdriver check alone lets it through and pollutes
// this same PostHog project with synthetic pageviews every probe cycle (play
// filed against unifind, applies here -- same init-gating pattern).
// 2026-10-16 (8b94d1d) fully excluded it from capture via isPrerenderPass,
// which also silenced the ONE signal the Control Center's own
// "analytics-probe-sportfund" heartbeat reads to know this pipeline is alive
// -- a false DOWN alert every cycle after that deploy (TSF-255). Tag instead
// of exclude: the probe still inits and its events still land (heartbeat
// stays truthful), but every event it sends carries is_synthetic_probe:true
// so human-traffic queries (visitor/pageview counts, funnels) can filter it
// out by that property instead of by its total absence.
const isConceptZoneProbe =
  typeof navigator !== 'undefined' && /ConceptZoneProbe/i.test(navigator.userAgent);

// A `npm run dev` session (developer or agent, e.g. http://localhost:5729/creator)
// runs this exact module and, until this guard existed, captured real events into
// the SAME production PostHog project real visitors report to. Found 2026-09-30:
// one such session contributed 73 $pageview events in a single week against a
// product whose real 7-day traffic is only ~54 visitor-days — enough to visibly
// skew the traffic trend the growth-hypothesis loop reads every run.
const isLocalhost =
  typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);

if (typeof window !== 'undefined' && posthogKey && !posthog.__loaded && !isPrerenderPass && !isLocalhost) {
  const consent =
    typeof document !== 'undefined'
      ? (document.cookie.match(/(?:^|;\s*)sportfund_consent=([^;]+)/)?.[1] ?? null)
      : null;

  posthog.init(posthogKey, {
    api_host: posthogHost,
    ui_host: 'https://eu.posthog.com',
    // `person_profiles:"always"` — otherwise the capture() gate short-circuits
    // for anonymous visitors (not identified + no groups) and silently drops
    // every pageview. Required for anonymous reach measurement to work.
    person_profiles: 'always',
    // PostHog's own default (capture_pageview:true) fires a $pageview ~1ms
    // after init on top of the one usePageTracking.ts already sends on mount —
    // confirmed live (run 110) as a landing-page double-$pageview on every
    // session. usePageTracking covers both the initial load and every SPA
    // route change, so autocapture here is pure duplication, not a fallback.
    // capture_pageleave defaults to "if_capture_pageview", which reads THIS
    // flag — pin it to true explicitly so turning off capture_pageview
    // doesn't silently kill $pageleave (engaged-time signal) as a side effect.
    capture_pageview: false,
    capture_pageleave: true,
    // Memory-only + no device writes until consent. On "Accept" the banner
    // flips these to localStorage+cookie / disable_persistence:false.
    persistence: consent === 'granted' ? 'localStorage+cookie' : 'memory',
    disable_persistence: consent !== 'granted',
    disable_session_recording: consent !== 'granted',
    // A declined visitor must make ZERO requests to PostHog. opt_out_capturing()
    // stops event capture but NOT feature-flag evaluation, which still POSTs to
    // /flags/ on first load — so "Decline disables all analytics" was true of
    // events and false of the network tab. Disable flags outright for them.
    advanced_disable_feature_flags: consent === 'denied',
    advanced_disable_feature_flags_on_first_load: consent === 'denied',
    // Keep exception autocapture (was enabled via the previous defaults preset).
    capture_exceptions: true,
    debug: import.meta.env.MODE === 'development',
    // See isConceptZoneProbe above: tag rather than exclude, so the probe's
    // own events keep landing (heartbeat stays truthful) while every
    // human-traffic query can exclude them by this property.
    before_send: isConceptZoneProbe
      ? (event) => {
          if (event) {
            event.properties = { ...event.properties, is_synthetic_probe: true };
          }
          return event;
        }
      : undefined,
  });

  if (consent === 'denied') {
    // Honour an explicit opt-out: wipe any stale device storage FIRST, then opt
    // out — order matters, because reset() clears the persisted opt-out flag, so
    // opting out afterwards makes the decision stick across reloads.
    posthog.reset(true);
    posthog.opt_out_capturing();
  }

  // Expose for debugging + reuse by lib/utils/analytics.ts window.posthog calls.
  (window as unknown as { posthog: typeof posthog }).posthog = posthog;
}

const tree = (
  <BrowserRouter>
    <AuthProvider>
      <App />
    </AuthProvider>
  </BrowserRouter>
);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PostHogProvider client={posthog}>{tree}</PostHogProvider>
  </StrictMode>
);

// Fund/mission pages fetch their OG title/image from Firestore asynchronously
// (see MissionPage.tsx's usePageMeta call), which never resolves within two
// rAFs. Firing render-event here for those routes would make the prerenderer
// snapshot the generic homepage OG tags instead of the athlete's own —
// MissionPage dispatches render-event itself once that fetch settles.
const isDataDrivenRoute = /^\/(fund|mission)\//.test(window.location.pathname);
if (!isDataDrivenRoute) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.dispatchEvent(new Event('render-event'));
    });
  });
}
